import createClient, { ClientOptions, FetchResponse, Middleware, ParseAsResponse } from 'openapi-fetch'
import type { MediaType, ResponseObjectMap, SuccessResponse } from 'openapi-typescript-helpers'
import { HttpError } from './HttpError'
import * as Scopes from '../constants/scopes'
import { withSpan } from '../telemetry/withSpan'
import { ConcurrencyThrottle } from '../utils/ConcurrencyThrottle'

import { type paths as museumV1Paths, PathsV1SearchGetParametersQueryType, UpdateModerationEventStatus } from './generated/museum-v1-types'
import type { paths as museumV2Paths } from './generated/museum-v2-types'
import type { paths as partylineV1Paths } from './generated/partyline-v1-types'
import type { paths as monObservabilityServiceV1Paths } from './generated/monitoring-web-observability-service-v1-types'
import {
  Address,
  AddressValidationRequest,
  AddressValidationResult,
  AlarmDetails,
  Camera,
  Contacts,
  CreateLocationChange,
  CreatePermitRequest,
  DispatchNotesDto,
  EmailAddresses,
  EngagementCameraRecording,
  GetAlarmsParams,
  GetEngagementsParams,
  GetLocationDeltasParams,
  GetMediatorUrlParams,
  GetSafewordsResponse,
  GetVideoUrlResponse,
  Incident,
  Location,
  LocationChange,
  LocationPermit,
  LocationPermitHistories,
  LocationPermittingComment,
  LocationPermittingCommentRequest,
  LocationPermittingComments,
  LocationSearchResponseDto,
  Metric,
  ModerationEvent,
  ModerationEventSourceDto,
  MonitoringServiceProviderLocation,
  PagedEngagements,
  PagedAlarmSummaries,
  PagedDeltaRecords,
  PatchMonitoring,
  PatchSecuritasLocationIdentifiers,
  PutTemplateIdDto,
  RecordingSegmentMetadata,
  RecordingType,
  SecuritasLocationIdentifiers,
  TemplateIdResponseDto,
  Token,
  UpdateLocationChange,
  UpdateSafewordRequest,
  UpdateSafewordResponse,
  Workflow,
  WorkflowStatus,
  Activity,
  PagedEngagementEvents,
  MonitoringSpecialistDto,
  OutdoorEngagement,
  AllEngagementRecordings,
  CameraRecording
} from './models'

/**
 * When running tests in node we rely on a fetch polyfill, but openapi-fetch constructs a custom request object that the polyfill doesn't recognize.
 * Intercepting the requests from and resubmitting them like this solves the issue
 */
const fetchForTests = ((input: Request) => {
  //this is a hacky thing we have to do for tests
  if (typeof input === 'string') {
    return fetch(input)
  } else {
    // Extract only the necessary properties to avoid AbortSignal compatibility issues
    return fetch(input.url, {
      method: input.method,
      headers: input.headers,
      body: input.body,
      credentials: input.credentials,
      cache: input.cache,
      redirect: input.redirect,
      referrer: input.referrer,
      integrity: input.integrity,
      mode: input.mode,
      // @ts-expect-error - duplex is required for requests with bodies in newer Node versions
      duplex: 'half'
    })
  }
}) as typeof fetch
const fetchFn = import.meta.env.MODE === 'test' ? fetchForTests : fetch
function applyFetchFnForTests(clientOptions: ClientOptions) {
  clientOptions.fetch = fetchFn
  return clientOptions
}

const endpoints = dex.config.endpoints
const museumV1Client = createClient<museumV1Paths>(applyFetchFnForTests({ baseUrl: endpoints.museum }))
const museumV2Client = createClient<museumV2Paths>(applyFetchFnForTests({ baseUrl: endpoints.museum }))
const partylineV1Client = createClient<partylineV1Paths>(applyFetchFnForTests({ baseUrl: endpoints.partyline }))
const monObservabilityServiceV1Client = createClient<monObservabilityServiceV1Paths>(
  applyFetchFnForTests({ baseUrl: endpoints.monitoringWebObservabilityService })
)

function wrapFetchWithMiddleware(fetchFn: typeof fetch, middleware: Middleware | undefined): typeof fetch {
  if (middleware) {
    return (async (url: string | URL | Request, init?: RequestInit | undefined) => {
      const request = new Request(url, init)
      // @ts-expect-error we aren't implementing the whole middleware spec, just what we need.
      await middleware?.onRequest?.({ request })
      return fetchFn(request)
    }) as unknown as typeof fetch
  } else {
    return fetchFn
  }
}

//This fetch function will behave just like regular fetch, but will inject the Auth token if registerAccessTokenMiddleware has been called
let fetchWithAuth = fetchFn

export function registerAccessTokenMiddleware(getAccessToken: () => Promise<string>) {
  const authMiddleware: Middleware = {
    async onRequest({ request }) {
      const accessToken = await getAccessToken()
      request.headers.set('Authorization', `Bearer ${accessToken}`)
      return request
    }
  }

  fetchWithAuth = wrapFetchWithMiddleware(fetch, authMiddleware)

  museumV1Client.use(authMiddleware)
  museumV2Client.use(authMiddleware)
  partylineV1Client.use(authMiddleware)
  monObservabilityServiceV1Client.use(authMiddleware)
}
export async function processResponse<T extends Record<string | number, unknown>, Options, Media extends MediaType>(
  res: Promise<FetchResponse<T, Options, Media>>
) {
  const {
    data, // only present if 2XX response
    error,
    response
  } = await res
  if (response.ok) {
    return data as Promise<ParseAsResponse<SuccessResponse<NonNullable<ResponseObjectMap<T>>, Media>, Options>>
  } else {
    throw new HttpError(response.status, `${response.status} error for ${response.url}`, error)
  }
}
export async function processFetchResponse<T>(responsePromise: Promise<Response>): Promise<T> {
  const res = await responsePromise
  const data: T = await res.json()
  if (res.ok && data) {
    return data as T
  } else {
    throw new HttpError(res.status, `${res.status} error for ${res.url}`)
  }
}

export async function makeApiCall<TApiCall, TResult>(
  // biome-ignore lint/complexity/noBannedTypes: Function is fine here because that's actually what we want
  callingFunction: Function,
  fetchFunction: () => Promise<TApiCall>,
  responseFn: (apiCall: Promise<TApiCall>) => Promise<TResult> = processResponse as (apiCall: Promise<TApiCall>) => Promise<TResult>
): Promise<TResult> {
  return withSpan(callingFunction.name, () => responseFn(fetchFunction()))
}

/**
 * Gets the location data associated with the provided location ID.
 *
 * Required scope: {@link Scopes.READ_LOCATIONS_SCOPE}
 *
 * @param locationId
 * @returns Promise<Location>
 */
export async function getLocation(locationId: string): Promise<Location> {
  return makeApiCall(getLocation, () =>
    museumV2Client.GET('/v2/locations/{locationId}', {
      params: {
        path: { locationId: locationId }
      }
    })
  )
}

/**
 * Gets the permit history for the permits associated with a location.
 *
 * Required scope: {@link Scopes.READ_LOCATIONS_SCOPE}
 *
 * @param locationId
 * @returns Promise<LocationPermitHistories>
 */
export async function getPermitHistory(locationId: string): Promise<LocationPermitHistories> {
  return makeApiCall(getPermitHistory, () =>
    museumV1Client.GET('/v1/locations/{locationId}/permits/history', {
      params: {
        path: { locationId: locationId }
      }
    })
  )
}

/**
 * Gets the permitting comments for a location.
 *
 * Required scope: {@link Scopes.READ_PERMITTING_COMMENTS_SCOPE}
 *
 * @param locationId
 * @returns Promise<LocationPermittingComments>
 */
export async function getPermittingComments(locationId: string): Promise<LocationPermittingComments> {
  return makeApiCall(getPermittingComments, () =>
    museumV1Client.GET('/v1/locations/{locationId}/permitting-comments', {
      params: {
        path: { locationId: locationId }
      }
    })
  )
}

/**
 * Creates a permitting comment for a location.
 *
 * Required scope: {@link Scopes.WRITE_PERMITTING_COMMENTS_SCOPE}
 *
 * @param locationId
 * @param request
 * @returns Promise<LocationPermittingComment>
 */
export async function createPermittingComment(locationId: string, request: LocationPermittingCommentRequest): Promise<LocationPermittingComment> {
  return makeApiCall(createPermittingComment, () =>
    museumV1Client.POST('/v1/locations/{locationId}/permitting-comments', {
      params: {
        path: { locationId: locationId }
      },
      body: request
    })
  )
}

/**
 * Creates a permit for the specified agency type for a location.
 *
 * Required scope: {@link Scopes.WRITE_LOCATIONS_SCOPE}
 *
 * @param locationId
 * @param agencyType
 * @param request
 * @returns Promise<LocationPermit>
 */
export async function createPermitByAgencyType(locationId: string, agencyType: string, request: CreatePermitRequest): Promise<LocationPermit> {
  return makeApiCall(createPermitByAgencyType, () =>
    museumV1Client.POST('/v1/locations/{locationId}/permits/{agencyType}', {
      params: {
        path: { locationId: locationId, agencyType: agencyType }
      },
      body: request
    })
  )
}

/**
 * Updates the permit for the specified agency type for a location.
 *
 * Required scope: {@link Scopes.WRITE_LOCATIONS_SCOPE}
 *
 * @param locationId
 * @param agencyType
 * @param request
 * @returns Promise<LocationPermit>
 */
export async function updatePermitByAgencyType(locationId: string, agencyType: string, request: CreatePermitRequest): Promise<LocationPermit> {
  return makeApiCall(updatePermitByAgencyType, () =>
    museumV1Client.PUT('/v1/locations/{locationId}/permits/{agencyType}', {
      params: {
        path: { locationId: locationId, agencyType: agencyType }
      },
      body: request
    })
  )
}

/**
 * Deletes the permit for the specified agency type for a location.
 *
 * Required scope: {@link Scopes.WRITE_LOCATIONS_SCOPE}
 *
 * @param locationId
 * @param agencyType
 * @returns Promise<void>
 */
export async function deletePermitByAgencyType(locationId: string, agencyType: string): Promise<void> {
  return makeApiCall(deletePermitByAgencyType, () =>
    museumV1Client.DELETE('/v1/locations/{locationId}/permits/{agencyType}', {
      params: {
        path: { locationId: locationId, agencyType: agencyType }
      }
    })
  )
}

/**
 * Gets the location ID associated with a monitoring transmitter code.
 *
 * Required scope: {@link Scopes.READ_LOCATIONS_SCOPE}
 *
 * @param systemNumber
 * @returns Promise<{ locationId: string }>
 */
export async function getSidFromSystemNumber(systemNumber: string): Promise<{
  locationId: string
}> {
  return makeApiCall(getSidFromSystemNumber, () =>
    museumV1Client.GET('/v1/monitoring-service-providers/{monitoringServiceProviderId}/locations/{transmitterCode}', {
      params: {
        path: {
          monitoringServiceProviderId: 'rrms-internal', //As of 9/5/2024, this isn't used on the backend so this value doesn't matter. However, I've started a convo with the backend team about it.
          transmitterCode: systemNumber
        }
      }
    })
  )
}

/**
 * Gets the location ID associated with a camera ID.
 *
 * Required scope: {@link Scopes.READ_LOCATIONS_SCOPE}
 *
 * @param cameraId
 * @returns Promise<LocationSearchResponseDto>
 */
export async function getSidFromCameraId(cameraId: string): Promise<LocationSearchResponseDto> {
  return makeApiCall(getSidFromCameraId, () =>
    museumV1Client.GET('/v1/search', {
      params: {
        query: {
          type: PathsV1SearchGetParametersQueryType.cameraId,
          id: cameraId
        }
      }
    })
  )
}

/**
 * Gets alarms for a location.
 *
 * Required scope: {@link Scopes.READ_ALARMS_SCOPE}
 *
 * @param sid
 * @param queryParams
 * @returns Promise<PagedAlarmSummaries>
 */
export async function getLocationAlarms(sid: string, queryParams?: GetAlarmsParams): Promise<PagedAlarmSummaries> {
  return makeApiCall(getLocationAlarms, () =>
    museumV1Client.GET('/v1/locations/{locationId}/alarms', {
      params: {
        path: {
          locationId: sid
        },
        query: queryParams
      }
    })
  )
}

/**
 * Gets a single alarm for a location.
 *
 * Required scope: {@link Scopes.READ_ALARMS_SCOPE}
 * Optional additional scope: {@link Scopes.READ_ALARM_RECORDINGS_SCOPE} to receive recording data when available.
 *
 * @param sid
 * @param alarmId
 * @returns Promise<AlarmDetails>
 */
export async function getLocationAlarm(sid: string, alarmId: string): Promise<AlarmDetails> {
  return makeApiCall(getLocationAlarm, () =>
    museumV1Client.GET('/v1/locations/{locationId}/alarms/{alarmId}', {
      params: {
        path: {
          locationId: sid,
          alarmId: alarmId
        }
      }
    })
  )
}

export async function getLocationAlarmSmsIncident(sid: string, alarmId: string): Promise<Incident> {
  return makeApiCall(getLocationAlarmSmsIncident, () =>
    partylineV1Client.GET('/v1/locations/{locationId}/incidents/{alarmId}', {
      params: {
        path: {
          locationId: sid,
          alarmId: alarmId
        }
      }
    })
  )
}

/**
 * Gets location changes for a location.
 *
 * Required scope: {@link Scopes.READ_LOCATION_CHANGE_SCOPE}
 *
 * @param sid
 * @returns Promise<LocationChange[]>
 */
export async function getLocationChanges(sid: string): Promise<LocationChange[]> {
  return makeApiCall(getLocationChanges, () =>
    museumV1Client.GET('/v1/locations/{locationId}/changes', {
      params: {
        path: {
          locationId: sid
        }
      }
    })
  )
}

/**
 * Gets paginated location deltas for a location.
 *
 * Required scope: {@link Scopes.READ_LOCATIONS_SCOPE}
 *
 * @param sid
 * @param queryParams
 * @returns Promise<PagedDeltaRecords>
 */
export async function getLocationDeltas(sid: string, queryParams?: GetLocationDeltasParams): Promise<PagedDeltaRecords> {
  return makeApiCall(getLocationDeltas, () =>
    museumV1Client.GET('/v1/locations/{locationId}/deltas', {
      params: {
        path: {
          locationId: sid
        },
        query: queryParams
      }
    })
  )
}

/**
 * Validates an address through Museum.
 *
 * Required scope: {@link Scopes.VALIDATE_ADDRESS_SCOPE}
 *
 * @param address
 * @returns Promise<AddressValidationResult>
 */
export async function validateAddress(address: AddressValidationRequest): Promise<AddressValidationResult> {
  return makeApiCall(validateAddress, () =>
    museumV1Client.POST('/v1/validation/validate-address', {
      body: address
    })
  )
}

/**
 * Creates a location change for a location.
 *
 * Required scope: {@link Scopes.WRITE_LOCATION_CHANGE_SCOPE}
 *
 * @param locationId
 * @param request
 * @returns Promise<LocationChange>
 */
export async function createLocationChange(locationId: string, request: CreateLocationChange): Promise<LocationChange> {
  return makeApiCall(createLocationChange, () =>
    museumV1Client.POST('/v1/locations/{locationId}/changes', {
      params: {
        path: { locationId: locationId }
      },
      body: request
    })
  )
}

/**
 * Updates a location change for a location.
 *
 * Required scope: {@link Scopes.WRITE_LOCATION_CHANGE_SCOPE}
 *
 * @param locationId
 * @param locationChangeId
 * @param locationChange
 * @returns Promise<LocationChange>
 */
export async function updateLocationChange(
  locationId: string,
  locationChangeId: string,
  locationChange: UpdateLocationChange
): Promise<LocationChange> {
  return makeApiCall(updateLocationChange, () =>
    museumV1Client.PUT('/v1/locations/{locationId}/changes/{locationChangeId}', {
      params: {
        path: { locationId: locationId, locationChangeId: locationChangeId }
      },
      body: locationChange
    })
  )
}

/**
 * Sends a metric to Museum.
 *
 * Required scope: {@link Scopes.WRITE_METRICS_SCOPE}
 *
 * @param metric
 * @returns Promise<void>
 */
export async function sendMetric(metric: Metric): Promise<void> {
  return makeApiCall(sendMetric, () =>
    museumV1Client.POST('/v1/metrics', {
      params: {},
      body: metric
    })
  )
}

export async function getRecordingSegmentDetails(engagementId: string, recordingType: RecordingType): Promise<RecordingSegmentMetadata[]> {
  return makeApiCall(getRecordingSegmentDetails, () =>
    monObservabilityServiceV1Client.GET('/v1/engagements/{engagementId}/recordings/{recordingType}/segments/metadata', {
      params: {
        path: {
          engagementId,
          recordingType
        }
      }
    })
  )
}

// Engagements can have thousands of recording segments. Firing a fetch for every one of
// them at once exhausts the browser's connection pool (ERR_INSUFFICIENT_RESOURCES), so we
// cap how many are in flight simultaneously. This is a single shared instance (rather than
// one per call) so the cap holds across concurrent recordings/engagements, not just within one.
const recordingSegmentThrottle = new ConcurrencyThrottle()

export async function getRecordingSegment(engagementId: string, recordingType: RecordingType, segmentId: string): Promise<Blob> {
  return recordingSegmentThrottle.throttle(async () => {
    const response = await fetchWithAuth(
      `${dex.config.endpoints.monitoringWebObservabilityService}/v1/engagements/${engagementId}/recordings/${recordingType}/segments/${segmentId}`
    )

    return response.blob()
  })
}

export type GetMediatorAccessTokenParams = { locationId: string; engagementId: string }
/**
 * Creates a mediator access token for an engagement recording request.
 *
 * Required scope: {@link Scopes.READ_ALARM_RECORDINGS_SCOPE}
 *
 * @param params
 * @returns Promise<Token>
 */
export async function getMediatorAccessToken(params: GetMediatorAccessTokenParams): Promise<Token> {
  return makeApiCall(getMediatorAccessToken, () =>
    museumV1Client.POST('/v1/locations/{locationId}/engagements/{engagementId}/token', {
      params: {
        path: params
      }
    })
  )
}
export type GetMediatorAccessTokenFn = typeof getMediatorAccessToken

/**
 * Gets a recording URL for an engagement camera clip.
 *
 * Required scope: {@link Scopes.READ_ALARM_RECORDINGS_SCOPE}
 *
 * @deprecated
 *
 * @param params
 * @returns Promise<EngagementCameraRecording>
 */
export async function getRecordingUrl(params: GetMediatorUrlParams): Promise<EngagementCameraRecording> {
  const { start, end, ...pathParams } = params
  return makeApiCall(getRecordingUrl, () =>
    museumV1Client.GET('/v1/locations/{locationId}/engagements/{engagementId}/recordings/cameras/{cameraId}', {
      params: { path: pathParams, query: { start, end } }
    })
  )
}
export type GetRecordingUrlFn = typeof getRecordingUrl

export async function getStreamingUrlFromMediator(mediatorUrl: string, accessToken: string, timeout = 5000): Promise<GetVideoUrlResponse> {
  const abortController = new AbortController()
  const res = fetchFn(mediatorUrl, {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal: abortController.signal
  })

  // In test mode, we don't want to implement the timeout because it messes with the test framework
  if (import.meta.env.MODE !== 'test') {
    const timer = setTimeout(() => abortController.abort({ message: `Mediator timed out after ${timeout} millis` }), timeout)
    res.finally(() => clearTimeout(timer))
  }

  return makeApiCall(
    getStreamingUrlFromMediator,
    () => res,
    response => processFetchResponse<GetVideoUrlResponse>(response)
  )
}
export type GetStreamingUrlFromMediatorFn = typeof getStreamingUrlFromMediator

export type MediatorFns = {
  getMediatorAccessToken: GetMediatorAccessTokenFn
  getRecordingUrl: GetRecordingUrlFn
  getStreamingUrl: GetStreamingUrlFromMediatorFn
}

/**
 * Gets moderation events for a location.
 *
 * Required scope: {@link Scopes.READ_MODERATION_EVENTS_SCOPE}
 *
 * @param locationId
 * @returns Promise<ModerationEvent[]>
 */
export async function getModerationEvents(locationId: string): Promise<ModerationEvent[]> {
  return makeApiCall(getModerationEvents, () =>
    museumV1Client.GET('/v1/locations/{locationId}/moderation-events', {
      params: {
        path: {
          locationId
        }
      }
    })
  )
}

/**
 * Creates a moderation event for a location.
 *
 * Required scope: {@link Scopes.WRITE_MODERATION_EVENTS_SCOPE}
 *
 * @param locationId
 * @param moderationEvent
 * @returns Promise<ModerationEvent>
 */
export async function createModerationEvent(locationId: string, moderationEvent: ModerationEventSourceDto): Promise<ModerationEvent> {
  return makeApiCall(createModerationEvent, () =>
    museumV1Client.POST('/v1/locations/{locationId}/moderation-events', {
      params: {
        path: { locationId }
      },
      body: moderationEvent
    })
  )
}

/**
 * Marks a moderation event as active.
 *
 * Required scope: {@link Scopes.WRITE_MODERATION_EVENTS_SCOPE}
 *
 * @param moderationEvent
 * @returns Promise<ModerationEvent>
 */
export async function patchModerationEvent(moderationEvent: ModerationEvent): Promise<ModerationEvent> {
  return makeApiCall(patchModerationEvent, () =>
    museumV1Client.PATCH('/v1/locations/{locationId}/moderation-events/{eventId}', {
      params: {
        path: { locationId: moderationEvent.locationId, eventId: moderationEvent.id }
      },
      body: {
        status: UpdateModerationEventStatus.Active
      }
    })
  )
}

/**
 * Gets cameras for a location.
 *
 * Required scope: {@link Scopes.READ_DEVICES_SCOPE}
 *
 * @param locationId
 * @returns Promise<Camera[]>
 */
export async function getCameras(locationId: string): Promise<Camera[]> {
  return makeApiCall(getCameras, () =>
    museumV1Client.GET('/v1/locations/{locationId}/cameras', {
      params: {
        path: {
          locationId
        }
      }
    })
  )
}

/**
 * Gets an engagement by ID.
 *
 * Required scope: {@link Scopes.READ_ENGAGEMENTS_SCOPE}
 *
 * @param engagementId
 * @returns Promise<Engagement>
 */
export async function getEngagement(engagementId: string): Promise<OutdoorEngagement> {
  return makeApiCall(getEngagement, () =>
    museumV1Client.GET('/v1/engagements/{engagementId}', {
      params: { path: { engagementId } }
    })
  )
}

/**
 * Gets paginated engagements.
 *
 * Required scope: {@link Scopes.READ_ENGAGEMENTS_SCOPE}
 *
 * @param queryParams
 * @returns Promise<PagedEngagements>
 */
export async function getEngagements(queryParams?: GetEngagementsParams): Promise<PagedEngagements> {
  return makeApiCall(getEngagements, () =>
    museumV1Client.GET('/v1/engagements', {
      params: {
        query: queryParams
      }
    })
  )
}

/**
 * Gets activities for an engagement.
 *
 * Required scope: {@link Scopes.READ_ENGAGEMENTS_SCOPE}
 *
 * @param engagementId
 * @returns Promise<Activity[]>
 */
export async function getEngagementActivities(engagementId: string): Promise<Activity[]> {
  return makeApiCall(getEngagementActivities, () =>
    museumV1Client.GET('/v1/engagements/{engagementId}/activities', {
      params: { path: { engagementId } }
    })
  )
}

/**
 * Gets the monitoring service provider location for a location.
 *
 * Required scope: {@link Scopes.READ_MSP_LOCATIONS_SCOPE}
 *
 * @param locationId
 * @returns Promise<MonitoringServiceProviderLocation>
 */
export async function getMonitoringServiceProviderLocation(locationId: string): Promise<MonitoringServiceProviderLocation> {
  return makeApiCall(getMonitoringServiceProviderLocation, () =>
    museumV1Client.GET('/v1/monitoring-service-provider-locations/{locationId}', {
      params: { path: { locationId } }
    })
  )
}

/**
 * Gets safewords for a location.
 *
 * Required scope: {@link Scopes.READ_LOCATION_SAFEWORD_SCOPE}
 *
 * @param locationId
 * @returns Promise<GetSafewordsResponse>
 */
export async function getSafewords(locationId: string): Promise<GetSafewordsResponse> {
  return makeApiCall(getSafewords, () =>
    museumV1Client.GET('/v1/locations/{locationId}/safe-words', {
      params: { path: { locationId } }
    })
  )
}

/**
 * Updates a safeword for a location.
 *
 * Required scope: {@link Scopes.WRITE_LOCATION_SAFEWORD_SCOPE}
 *
 * @param locationId
 * @param id
 * @param req
 * @returns Promise<UpdateSafewordResponse>
 */
export async function updateSafeword(locationId: string, id: string, req: UpdateSafewordRequest): Promise<UpdateSafewordResponse> {
  return makeApiCall(updateSafeword, () =>
    museumV1Client.PUT('/v1/locations/{locationId}/safe-words/{safeWordId}', {
      params: {
        path: {
          locationId,
          safeWordId: id
        }
      },
      body: req
    })
  )
}

/**
 * Updates the template ID for a location.
 *
 * Required scope: {@link Scopes.WRITE_LOCATION_TEMPLATE_ID_SCOPE}
 *
 * @param locationId
 * @param req
 * @returns Promise<TemplateIdResponseDto>
 */
export async function updateTemplateId(locationId: string, req: PutTemplateIdDto): Promise<TemplateIdResponseDto> {
  return makeApiCall(updateTemplateId, () =>
    museumV1Client.PUT('/v1/locations/{locationId}/template-id', {
      params: {
        path: {
          locationId
        }
      },
      body: req
    })
  )
}

/**
 * Gets monitoring service provider identifiers for a location.
 *
 * Required scope: {@link Scopes.READ_LOCATION_MSP_IDENTIFIERS_SCOPE}
 *
 * @param locationId
 * @returns Promise<SecuritasLocationIdentifiers>
 */
export async function getSecuritasIdentifiers(locationId: string): Promise<SecuritasLocationIdentifiers> {
  return makeApiCall(getSecuritasIdentifiers, () =>
    museumV1Client.GET('/v1/monitoring-service-provider-locations/{locationId}/identifiers', {
      params: {
        path: {
          locationId
        }
      }
    })
  )
}

/**
 * Updates monitoring service provider identifiers for a location.
 *
 * Required scope: {@link Scopes.WRITE_LOCATION_MSP_IDENTIFIERS_SCOPE}
 *
 * @param locationId
 * @param req
 * @returns Promise<PatchSecuritasLocationIdentifiers>
 */
export async function updateSecuritasIdentifiers(
  locationId: string,
  req: PatchSecuritasLocationIdentifiers
): Promise<PatchSecuritasLocationIdentifiers> {
  return makeApiCall(updateSecuritasIdentifiers, () =>
    museumV1Client.PATCH('/v1/monitoring-service-provider-locations/{locationId}/identifiers', {
      params: {
        path: {
          locationId
        }
      },
      body: req
    })
  )
}

/**
 * Updates dispatch notes for a location.
 *
 * Required scope: {@link Scopes.WRITE_LOCATION_DISPATCH_NOTES_SCOPE}
 *
 * @param locationId
 * @param req
 * @returns Promise<DispatchNotesDto>
 */
export async function updateDispatchNotes(locationId: string, req: DispatchNotesDto): Promise<DispatchNotesDto> {
  return makeApiCall(updateDispatchNotes, () =>
    museumV1Client.PUT('/v1/locations/{locationId}/dispatch-notes', {
      params: {
        path: {
          locationId
        }
      },
      body: req
    })
  )
}

/**
 * Updates contacts for a location.
 *
 * Required scope: {@link Scopes.WRITE_LOCATION_CONTACTS_SCOPE}
 *
 * @param locationId
 * @param contacts
 * @returns Promise<Contacts>
 */
export async function updateContacts(locationId: string, contacts: Contacts): Promise<Contacts> {
  return makeApiCall(updateContacts, () =>
    museumV1Client.PUT('/v1/locations/{locationId}/contacts', {
      params: {
        path: {
          locationId
        }
      },
      body: contacts
    })
  )
}

/**
 * Toggles practice mode on for a specified timer period or off indefinitely.
 *
 * Required scope: {@link Scopes.WRITE_LOCATION_MONITORING_SCOPE}
 *
 * @param locationId
 * @param request
 * @returns Promise<PatchMonitoring>
 */
export async function togglePracticeMode(locationId: string, request: PatchMonitoring): Promise<PatchMonitoring> {
  return makeApiCall(togglePracticeMode, () =>
    museumV1Client.PUT('/v1/locations/{locationId}/monitoring', {
      params: {
        path: { locationId: locationId }
      },
      body: request
    })
  )
}

/**
 * Tells the back end to send the monitoring certificate to the specified email addresses.
 *
 * Required scope: {@link Scopes.WRITE_MONITORING_CERTIFICATE_SCOPE}
 *
 * @param locationId
 * @param request
 * @returns Promise<EmailAddresses>
 */
export async function triggerSendingOfCertificateEmail(locationId: string, request: EmailAddresses): Promise<EmailAddresses> {
  return makeApiCall(triggerSendingOfCertificateEmail, () =>
    museumV1Client.POST('/v1/locations/{locationId}/certificates/monitoring/email', {
      params: {
        path: { locationId: locationId }
      },
      body: request
    })
  )
}

/**
 * Gets the monitoring certificate associated with a location.
 *
 * Required scope: {@link Scopes.WRITE_MONITORING_CERTIFICATE_SCOPE}
 *
 * @param locationId
 * @returns Promise<Blob>
 */
export async function getMonitoringCertificateByLocationId(locationId: string): Promise<Blob> {
  const response = await fetchWithAuth(`${dex.config.endpoints.museum}/v1/locations/${locationId}/certificates/monitoring`)
  if (!response.ok) {
    throw new Error(`Failed to fetch monitoring certificate: ${response.status}`)
  }
  return response.blob()
}

/**
 * Saves the address for a location.
 *
 * Required scope: {@link Scopes.WRITE_LOCATION_ADDRESS_SCOPE}
 *
 * @param locationId
 * @param address
 * @returns Promise<Address>
 */
export async function saveAddress(locationId: string, address: Address): Promise<Address> {
  return makeApiCall(saveAddress, () =>
    museumV1Client.PUT('/v1/locations/{locationId}/address', {
      params: {
        path: { locationId: locationId }
      },
      body: address
    })
  )
}

/**
 * Gets workflows for a location, optionally filtered by status.
 * This is used to determine if there are any pending workflows for a location, such as an address change.
 *
 * Required scope: {@link Scopes.READ_LOCATION_WORKFLOW_SCOPE}
 *
 * @param locationId
 * @param workflowStatus
 * @returns Promise<Workflow[]>
 */
export async function getProvisioningWorkflowsByStatus(locationId: string, workflowStatus: WorkflowStatus): Promise<Workflow[]> {
  return makeApiCall(getProvisioningWorkflowsByStatus, () =>
    museumV1Client.GET('/v1/locations/{locationId}/workflows', {
      params: {
        path: { locationId: locationId },
        query: { status: workflowStatus }
      }
    })
  )
}

/**
 * Gets the specialist assigned to an engagement.
 *
 * Required scope: {@link Scopes.READ_SPECIALISTS_SCOPE}
 *
 * @param monitoringServiceProviderId
 * @param specialistId
 * @returns Promise<MonitoringSpecialistDto>
 */
export async function getSpecialist(monitoringServiceProviderId: string, specialistId: string): Promise<MonitoringSpecialistDto> {
  return makeApiCall(getSpecialist, () =>
    museumV1Client.GET('/v1/specialists/{monitoringServiceProviderId}/{specialistId}', {
      params: {
        path: { monitoringServiceProviderId, specialistId }
      }
    })
  )
}

/**
 * Gets the events associated with an engagement.
 *
 * Required scope: {@link Scopes.READ_ENGAGEMENTS_SCOPE}
 *
 * @param engagementId
 * @returns Promise<PagedEngagementEvents>
 */
export async function getEngagementEvents(engagementId: string): Promise<PagedEngagementEvents> {
  return makeApiCall(getEngagementEvents, () =>
    museumV1Client.GET('/v1/engagements/{engagementId}/events', {
      params: { path: { engagementId } }
    })
  )
}

/**
 * Gets the recordings associated with an engagement.
 *
 * Required scope: {@link Scopes.READ_ALARM_RECORDINGS_SCOPE}
 *
 * @param locationId
 * @param engagementId
 * @returns Promise<EngagementRecordings>
 */
export async function getAllEngagementRecordings(locationId: string, engagementId: string): Promise<AllEngagementRecordings> {
  return makeApiCall(getAllEngagementRecordings, () =>
    museumV2Client.GET('/v2/locations/{locationId}/engagements/{engagementId}/recordings', {
      params: { path: { locationId, engagementId } }
    })
  )
}

/**
 * Gets the recordings associated with a specific camera on an engagement.
 *
 * Required scope: {@link Scopes.READ_ALARM_RECORDINGS_SCOPE}
 *
 * @param locationId
 * @param engagementId
 * @param cameraId
 * @param start
 * @param end
 * @returns Promise<CameraRecording>
 */
export async function getEngagementCameraRecording(
  locationId: string,
  engagementId: string,
  cameraId: string,
  start: string,
  end: string
): Promise<CameraRecording> {
  return makeApiCall(getEngagementCameraRecording, () =>
    museumV2Client.GET('/v2/locations/{locationId}/engagements/{engagementId}/recordings/cameras/{cameraId}', {
      params: { path: { locationId, engagementId, cameraId }, query: { start, end } }
    })
  )
}
